import React, { useEffect, useRef } from 'react';
import { ImageSize } from 'types/isomorphic';
import useOnLoad from '../../../hooks/queries/useOnLoad';
import useSelectedIndex from '../../../hooks/stateful/useSelectedIndex';
import useDownloadSize from '../../../hooks/stateful/useDownloadSize';
import useDownloadModifiedAsset from '../../../hooks/useDownloadModifiedAsset';
import useImageDownloadSizes from '../../../shared/api/download/useImageDownloadSizesRequest';
import { SIZE_NAMES } from '../../../constants/sizeNames';
import DownloadSizePreview from '../../../components/GenAssetAcquisitionCard/DownloadSizePreview/DownloadSizePreview';
import isExtendEdit from '../../../shared/isExtendEdit';
import pickLicensedFiboSize from './pickLicensedFiboSize';
import styles from './LicensedModifiedSize.module.scss';

const DEFAULT_DPI = '72';

interface LicensedOptionSize {
  name?: string;
  pixelWidth?: number | string;
  pixelHeight?: number | string;
  dpi?: number | string;
  contentType?: string;
  modifiedSize?: ImageSize | null;
}
interface OptionWithSizes {
  sizes?: LicensedOptionSize[];
}

// Module-scope helpers keep the component's cyclomatic complexity within
// budget (mirroring the pattern the legacy GenAssetLicensedSize used).

// An extra-small (comp) license takes the small/1k tier via the same
// substitution the legacy size lookup applied, instead of falling through to
// the largest.
const resolveSizeToFind = (licensedSizeName?: string): string =>
  licensedSizeName && licensedSizeName !== SIZE_NAMES.extraSmall
    ? licensedSizeName
    : SIZE_NAMES.small;

const findLicensedOriginal = (
  options: OptionWithSizes[],
  selectedOptionIndex: number,
  sizeToFind: string
): LicensedOptionSize | undefined =>
  options[selectedOptionIndex]?.sizes?.find(
    (sizeObj) => sizeObj.name === sizeToFind
  );

// Extend uses only the load-data join (missing => undefined, matching legacy —
// never a fibo largest-fallback that over-serves); fibo/pre-shot has no join, so
// resolve from /download-sizes.
const resolveModifiedSize = (
  licensedOriginal: LicensedOptionSize | undefined,
  downloadSizes: ImageSize[] | undefined,
  sizeToFind: string
): ImageSize | undefined =>
  isExtendEdit(downloadSizes)
    ? licensedOriginal?.modifiedSize ?? undefined
    : pickLicensedFiboSize(downloadSizes, sizeToFind);

const originalPreview = (original?: LicensedOptionSize) =>
  original?.pixelWidth && original?.pixelHeight ? (
    <DownloadSizePreview
      sizeObject={{
        width: String(original.pixelWidth),
        height: String(original.pixelHeight),
        dpi: String(original.dpi ?? DEFAULT_DPI),
      }}
      fileType={original.contentType?.toUpperCase()}
    />
  ) : null;

const modifiedPreview = (modifiedSize?: ImageSize) =>
  modifiedSize ? (
    <DownloadSizePreview
      sizeObject={{
        width: String(modifiedSize.width),
        height: String(modifiedSize.height),
        dpi: '',
      }}
      isModified
      fileType={modifiedSize.fileType?.toUpperCase()}
    />
  ) : null;

/**
 * The redownload counterpart of ModifiedBuyCard: no size selector — the
 * purchased original/modified pair is summarized read-only (same visual
 * language as the completed-download view) and seeded into useDownloadSize so
 * the redownload button requests the licensed size.
 */
const LicensedModifiedSize = () => {
  const {
    response: { config: { options = [] } = {} } = {},
  }: { response?: { config?: { options?: OptionWithSizes[] } } } = useOnLoad();
  const { selectedOptionIndex = 0, selectedSize } = useSelectedIndex();
  const { assetLicense, genId, index } = useDownloadModifiedAsset();
  const { setDownloadSize } = useDownloadSize();
  const { getDownloadSizes } = useImageDownloadSizes();
  // Shares useDownloadSize's query key, so React Query serves it from cache —
  // no extra request.
  const { data: downloadSizes } = getDownloadSizes(
    genId as string,
    index as number
  );

  const sizeToFind = resolveSizeToFind(assetLicense?.sizeName);
  const licensedOriginal = findLicensedOriginal(
    options,
    selectedOptionIndex,
    sizeToFind
  );
  const modifiedSize = resolveModifiedSize(
    licensedOriginal,
    downloadSizes,
    sizeToFind
  );

  // Key the seeding effect on the primitive sizeName (as the legacy
  // GenAssetLicensedSize did) so a new-but-equal object — e.g. after a
  // /download-sizes refetch — doesn't re-fire it; the ref carries the latest
  // object without widening the dependency list.
  const modifiedSizeRef = useRef(modifiedSize);
  modifiedSizeRef.current = modifiedSize;
  const modifiedSizeName = modifiedSize?.sizeName;

  // An undefined modifiedSize (query still in flight) is still seeded: the
  // useDownloadSize guard treats it as "not known yet" without invalidating a
  // completed download.
  useEffect(() => {
    if (!assetLicense) return;
    setDownloadSize(modifiedSizeRef.current);
  }, [assetLicense, modifiedSizeName, setDownloadSize]);

  if (!assetLicense) return null;

  return (
    <div
      className={styles.licensedModifiedSize}
      data-testid="licensed-modified-size"
    >
      {originalPreview(licensedOriginal ?? selectedSize)}
      {modifiedPreview(modifiedSize)}
    </div>
  );
};

export default LicensedModifiedSize;
